import { C } from "@/brand/tokens";
import { inquiryFlow } from "@/brand/content";
import { ChamberChrome } from "@/components/common/ChamberChrome";

/**
 * InquiryFlowSection — Dry Stone chamber (VI. THE PROCEDURE).
 *
 * The staggered vertical-axis composition is already the strongest
 * architectural moment on the page. Authoring moves added here:
 *   - Chamber chrome rail at top (Roman numeral VI.)
 *   - Section number tag and intro tightened
 */
export function InquiryFlowSection() {
  return (
    <section
      id="inquiry-flow"
      data-testid="inquiry-flow"
      style={{
        background: C.surfaceLight,
        color: C.textOnLight,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <ChamberChrome
        numeral="VI."
        title="The procedure"
        category="Inquiry flow"
        meta="Five steps"
        tone="light"
      />

      <div className="mx-auto max-w-[1280px] px-5 sm:px-8 lg:px-14 py-24 lg:py-32">
        {/* Intro rail */}
        <div className="grid lg:grid-cols-12 gap-10 mb-16">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-7">
              <span
                aria-hidden="true"
                className="inline-block h-px w-10"
                style={{ background: C.accentMetal }}
              />
              <p
                className="font-inter text-[11px] uppercase font-semibold tabular-nums"
                style={{ color: C.accentMetal, letterSpacing: "0.28em" }}
              >
                § 06
              </p>
            </div>
            <h2
              className="font-cormorant"
              style={{
                color: C.textOnLight,
                fontSize: "clamp(2.2rem, 4vw, 3.4rem)",
                fontWeight: 400,
                lineHeight: 1.04,
                letterSpacing: "-0.012em",
              }}
            >
              A clear path
              <br />
              from inquiry
              <br />
              to engagement.
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-6">
            <p
              className="font-inter leading-[1.7] max-w-[44ch]"
              style={{ color: C.inkOnLight, fontSize: "1rem" }}
            >
              Reaching out shouldn’t feel like a leap. Here is exactly what
              happens between the moment you submit an inquiry and the start
              of the work.
            </p>
          </div>
        </div>

        {/* Staggered axis sequence */}
        <div className="relative">
          {/* Central vertical axis — visible on lg+ */}
          <span
            aria-hidden="true"
            className="hidden lg:block absolute top-0 bottom-0"
            style={{
              left: "calc(50% - 0.5px)",
              width: 1,
              background: C.borderSoft,
            }}
          />

          <ol data-testid="inquiry-flow-list" className="space-y-0">
            {inquiryFlow.map((p, i) => {
              const onLeft = i % 2 === 0; // 01, 03, 05 left; 02, 04 right
              return (
                <li
                  key={p.n}
                  data-testid={`inquiry-step-${i}`}
                  className="relative py-10 lg:py-14"
                >
                  {/* Mobile: vertical brass axis on far left */}
                  <span
                    aria-hidden="true"
                    className="lg:hidden absolute top-0 bottom-0"
                    style={{ left: 28, width: 1, background: C.borderSoft }}
                  />

                  {/* Desktop grid — milestone + axis intersection */}
                  <div className="hidden lg:grid grid-cols-12 items-center">
                    {/* LEFT slot — col 1-5 */}
                    <div className="col-span-5 pr-12">
                      {onLeft && <MilestoneCard step={p} alignRight />}
                    </div>

                    {/* CENTER axis number — col 6 */}
                    <div className="col-span-2 flex items-center justify-center">
                      {onLeft ? (
                        <span
                          aria-hidden="true"
                          className="block h-px"
                          style={{
                            width: "100%",
                            background: C.accentMetal,
                            marginRight: "-1rem",
                          }}
                        />
                      ) : (
                        <span
                          aria-hidden="true"
                          className="block h-px"
                          style={{
                            width: "100%",
                            background: C.accentMetal,
                            marginLeft: "-1rem",
                          }}
                        />
                      )}
                      <span
                        className="absolute font-cormorant tabular-nums px-4"
                        style={{
                          color: C.accentMetal,
                          fontSize: "clamp(2rem, 3.4vw, 2.8rem)",
                          fontWeight: 400,
                          lineHeight: 1,
                          background: C.surfaceLight,
                          left: "calc(50% - 1.5rem)",
                        }}
                      >
                        {p.n}
                      </span>
                    </div>

                    {/* RIGHT slot — col 8-12 */}
                    <div className="col-span-5 pl-12">
                      {!onLeft && <MilestoneCard step={p} />}
                    </div>
                  </div>

                  {/* Mobile layout — all content right of axis */}
                  <div className="lg:hidden pl-16 relative">
                    <div
                      aria-hidden="true"
                      className="absolute"
                      style={{
                        left: 28,
                        top: "1.5rem",
                        width: 24,
                        height: 1,
                        background: C.accentMetal,
                      }}
                    />
                    <span
                      className="absolute font-cormorant tabular-nums"
                      style={{
                        left: 14,
                        top: "0.25rem",
                        color: C.accentMetal,
                        fontSize: "1.8rem",
                        fontWeight: 400,
                        lineHeight: 1,
                        background: C.surfaceLight,
                        padding: "0 4px",
                      }}
                    >
                      {p.n}
                    </span>
                    <MilestoneCard step={p} />
                  </div>
                </li>
              );
            })}
          </ol>
        </div>

        <p
          className="mt-16 font-inter text-[12.5px] leading-[1.7] max-w-[60ch]"
          style={{ color: C.inkOnLightSoft }}
        >
          Every inquiry is handled by Katrina directly. If Faith&nbsp;1st is
          not the right fit, we’ll often refer you to someone better suited.
        </p>
      </div>
    </section>
  );
}

/**
 * MilestoneCard — single milestone (label + body) used inside the axis
 * sequence. Architectural typography only; no border/background.
 */
function MilestoneCard({ step, alignRight = false }) {
  return (
    <div className={alignRight ? "lg:text-right" : ""}>
      <p
        className="font-inter text-[10.5px] uppercase font-semibold mb-3"
        style={{ color: C.accentMetal, letterSpacing: "0.28em" }}
      >
        {step.label}
      </p>
      <p
        className="font-cormorant"
        style={{
          color: C.textOnLight,
          fontSize: "clamp(1.05rem, 1.25vw, 1.2rem)",
          lineHeight: 1.4,
          fontStyle: "italic",
          fontWeight: 400,
        }}
      >
        {step.body}
      </p>
    </div>
  );
}
